import { action, computed, makeObservable, observable, transaction } from 'mobx'
import {
  IStateViewModel,
  StateViewModelOptions,
  StateViewModelParams,
} from './mobxUtils.types'
import cloneDeep from 'lodash/cloneDeep'

export class StateViewModel<T> implements IStateViewModel<T> {
  initStateSchema: Partial<T> = undefined as unknown as Partial<T>
  initialState: T = undefined as unknown as T
  state: T = undefined as unknown as T
  private options: StateViewModelOptions = {
    observableType: 'deep',
    clone: true,
  }

  constructor(params?: StateViewModelParams<T>) {
    if (params?.options) {
      this.options = {
        ...this.options,
        ...params.options,
      }
    }

    makeObservable(this, {
      state: this.getObservableType(this.options.observableType),
      initialState: this.getObservableType(this.options.observableType),
      initStateSchema: this.getObservableType(this.options.observableType),
      setState: action.bound,
      initState: action.bound,
      initDefaultState: action.bound,
      updateState: action.bound,
      resetState: action.bound,
      clearState: action.bound,
      defaultState: computed,
    })
    if (params?.state) {
      this.initStateSchema = this.clone(params.state)
      this.initState(params.state)
    }
  }

  private getObservableType = (
    observableType: StateViewModelOptions['observableType']
  ) => {
    switch (observableType) {
      case 'shallow':
        return observable.shallow
      case 'deep':
        return observable.deep
      case 'struct':
        return observable.struct
      case 'ref':
        return observable.ref
      default:
        return observable.deep
    }
  }

  setState = (state: T) => {
    this.state = this.clone(state)
  }

  initState = (initialState: T) => {
    this.initialState = this.clone(initialState)
    this.state = this.clone(initialState)
  }

  initInitialState = (initialState: T) => {
    this.initialState = this.clone(initialState)
  }

  get defaultState() {
    return this.initStateSchema
  }

  initDefaultState = (state: Partial<T>) => {
    this.initStateSchema = this.clone(state)
  }

  /**
   * Updates the state with the provided partial state or updater function.
   *
   * If a partial state object is provided, only the specified fields are updated,
   * and the other fields in the state remain unchanged.
   * If an updater function is provided, it receives the current state and returns
   * a new state object. Only the fields that differ from the current state will be updated.
   *
   * @param stateOrUpdater - A partial state object or a function that returns a new state.
   * @param isForceInitUpdate - If true, updates the initialState with the new values as well.
   *
   * @example
   * // Update specific fields without affecting others
   * updateState({ name: "Alice" });
   * // this.state will be { name: "Alice", age: 17 } if the previous state was { name: "John", age: 17 }
   *
   * @example
   * // Use an updater function to modify the state
   * updateState(prevState => ({ ...prevState, name: "Alice" }));
   * // this.state will be { name: "Alice", age: 17 } if the previous state was { name: "John", age: 17 }
   */

  updateState = (
    stateOrUpdater: Partial<T> | ((prevState: T) => T),
    isForceInitUpdate = false
  ) => {
    let newState: Partial<T>
    const currentState: T = this.clone(this.state)
    if (typeof stateOrUpdater === 'function') {
      newState = (stateOrUpdater as (prevState: T) => T)(currentState)
    } else if (
      !currentState ||
      (typeof stateOrUpdater === 'object' && Array.isArray(stateOrUpdater)) ||
      this.isPrimitive(stateOrUpdater)
    ) {
      this.state = this.clone(stateOrUpdater as T)
      if (isForceInitUpdate) {
        this.initialState = this.clone(stateOrUpdater as T)
      }
      return
    } else {
      newState = this.clone(stateOrUpdater)
    }

    transaction(() => {
      Object.keys(newState).forEach(
        action((key) => {
          const newValue = newState[key]
          if (currentState[key] !== newValue) {
            if (newValue === undefined) {
              delete this.state[key]
              if (isForceInitUpdate) {
                delete this.initialState[key]
              }
              return
            }

            this.state[key] = this.clone(newValue)
            if (isForceInitUpdate) {
              this.initialState[key] = this.clone(newValue)
            }
          }
        })
      )
    })
  }

  resetState = () => {
    console.log('resetState', this.initialState)
    if (this.initialState) {
      this.state = this.clone(this.initialState)
    } else {
      this.clearState()
    }
  }

  clearState = () => {
    this.initStateSchema = this.clone(this.initStateSchema) as T
    this.state = this.clone(this.initStateSchema) as T
  }

  private clone = <U>(data: U): U => {
    return this.options.clone ? cloneDeep(data) : data
  }

  private isPrimitive = <T>(value: T): boolean => {
    return value !== Object(value)
  }
}
